import { createContext, ReactNode, useCallback, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { writeArticleClusteringToStorage } from '@/core/constants/preferences';
import { useAppDispatch } from '@/core/store/store';

import { getUserSelector } from '@/features/Auth/store';
import {
  hydrateArticleClusteringPreference,
  updateArticleClusteringPreference,
} from '@/features/UserPreferences/store';
import { setArticleClusteringEnabled } from '@/features/UserPreferences/store/slice';
import {
  getArticleClusteringEnabledSelector,
  isArticleClusteringPreferenceLoadedSelector,
} from '@/features/UserPreferences/store/selectors';

export type UserPreferencesContextType = {
  articleClusteringEnabled: boolean;
  setArticleClusteringEnabled: (value: boolean) => void;
  isArticleClusteringPreferenceLoaded: boolean;
};

export const UserPreferencesContext = createContext<UserPreferencesContextType | null>(null);

type UserPreferencesProviderProps = {
  children: ReactNode;
};

export const UserPreferencesProvider = ({ children }: UserPreferencesProviderProps) => {
  const dispatch = useAppDispatch();
  const user = useSelector(getUserSelector);
  const articleClusteringEnabled = useSelector(getArticleClusteringEnabledSelector);
  const isArticleClusteringPreferenceLoaded = useSelector(
    isArticleClusteringPreferenceLoadedSelector
  );

  useEffect(() => {
    dispatch(hydrateArticleClusteringPreference());
  }, []);

  useEffect(() => {
    if (typeof user?.articleClusteringEnabled !== 'boolean') {
      return;
    }

    dispatch(setArticleClusteringEnabled(user.articleClusteringEnabled));
    writeArticleClusteringToStorage(user.articleClusteringEnabled);
  }, [dispatch, user?.articleClusteringEnabled]);

  const handleArticleClusteringEnabledChange = useCallback(
    (value: boolean) => {
      dispatch(updateArticleClusteringPreference(value));
    },
    [dispatch]
  );

  return (
    <UserPreferencesContext.Provider
      value={{
        articleClusteringEnabled,
        setArticleClusteringEnabled: handleArticleClusteringEnabledChange,
        isArticleClusteringPreferenceLoaded,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);

  if (!context) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }

  return context;
};
